import { gsap, ScrollTrigger } from "@/composeables/gsap"

function waitForCanPlay(video) {
    return new Promise((resolve) => {
        if (video.readyState >= 3) {
            resolve(video);
        } else {
            video.addEventListener("canplay", () => resolve(video), { once: true });
        }
    });
}

function createZoomAnimation(section) {
    const qs = gsap.utils.selector(section);
    const media = qs(".section-text-on-image__media .media");
    const overlay = qs(".section-text-on-image__media-overlay");
    const video = section.querySelector('video[data-scroll-trigger="true"]')

    gsap.set(overlay, {
        willChange: 'opacity'
    })
    gsap.set(media, {
        willChange: 'transform'
    })

    const fadeOutOverlay = gsap.fromTo(
        overlay,
        { autoAlpha: 1 },
        { autoAlpha: 0.3 }
    )
    const scaleMedia = gsap.fromTo(
        media,
        { scale: 2.5 },
        { scale: 1 }
    );

    const timeline = gsap
        .timeline()
        .addLabel("start")
        .add([fadeOutOverlay, scaleMedia])
        .addLabel("end")

    const trigger = ScrollTrigger.create({
        trigger: section,
        markers: false,
        start: "top bottom",
        end: "top top",
        invalidateOnRefresh: true,
        animation: timeline,
        scrub: true,
    });

    if (!video) { return }

    const videoTrigger = ScrollTrigger.create({
        trigger: section,
        start: "top bottom",
        end: "bottom top",
        markers: false,
        invalidateOnRefresh: true,
        onEnter(self) {
            waitForCanPlay(video)
                .then((video) => video.play())
        },
        onLeave(self) {
            video.pause()
        },
        onLeaveBack(self) {
            video.pause()
        },
        onEnterBack(self) {
            waitForCanPlay(video)
                .then((video) => video.play())
        },

    })

    if (videoTrigger.isActive) {
        waitForCanPlay(video)
            .then((video) => video.play())
    } else {
        video.pause()
    }

}

function createRevealAnimation(section) {
    const qs = gsap.utils.selector(section);
    const media = qs(".section-text-on-image__media");
    const overlay = qs(".section-text-on-image__media-overlay");
    const content = qs(".section-text-on-image__content");

    gsap.set(media, {
        willChange: 'transform',
        transformOrigin: 'center center'
    })
    gsap.set(overlay, {
        willChange: 'opacity'
    })
    gsap.set(content, {
        autoAlpha: 0,
        willChange: 'transform, opacity'
    })

    // Eine durchgehend gescrubbte Timeline über den Einscroll-Weg — bewusst
    // ohne Pin, damit beim Scrollen keine Leerräume/Standzeiten entstehen.
    // Während der Einfahrt liegt die Section von Natur aus an der
    // Viewport-Unterkante an: erst wächst das Medium auf Zielgröße (erste
    // Hälfte), dann dunkelt es ab, zuletzt zieht der Text zentriert ein —
    // fertig, sobald die Section vollständig sichtbar ist, ab da scrollt
    // alles zusammen weiter.
    const timeline = gsap
        .timeline()
        .addLabel("start")
        .fromTo(media, { scale: 0.6 }, { scale: 1, duration: 2, ease: "none" })
        .fromTo(overlay, { autoAlpha: 0 }, { autoAlpha: 0.6, duration: 1 })
        .fromTo(
            content,
            { autoAlpha: 0, yPercent: 25 },
            { autoAlpha: 1, yPercent: 0, duration: 1 }
        )
        .addLabel("end")

    ScrollTrigger.create({
        trigger: section,
        markers: false,
        start: "top bottom",
        end: "bottom bottom",
        invalidateOnRefresh: true,
        animation: timeline,
        scrub: true,
    });
}

function createParallaxAnimation(section) {
    const qs = gsap.utils.selector(section);
    const media = qs(".media img");

    const direction = 1;
    const percent = 30;
    const scale = 1.6;

    gsap.set(media, {
        willChange: 'transform'
    })

    const tween = gsap.fromTo(
        media,
        {
            y: (1 * direction * percent) + '%',
            scale: scale,
        },
        {
            y: (-1 * direction * percent) + '%',
            scale: scale,
            ease: "none",
        }
    )

    const timeline = gsap
        .timeline()
        .addLabel("start")
        .add(tween)
        .addLabel("end")

    ScrollTrigger.create({
        trigger: section,
        markers: false,
        start: "top bottom",
        end: "bottom top",
        invalidateOnRefresh: true,
        animation: timeline,
        scrub: true,
    });
}

// Media-Stack: mehrere 1:1 übereinandergelegte Bilder. Beim Scrollen driften die
// Layer sehr subtil und überwiegend vertikal (viel Y, wenig X, von unten nach oben),
// gestaffelt nach Tiefe (hinterster Layer am wenigsten, jeder weitere davor etwas
// mehr → dezenter Tiefeneindruck). Der Focal Point steuert je Layer den Anker der
// leichten Skalierung: Focal Point oben → unterer Teil ragt weiter aus der Box.
// Zusätzlich: die unterste Ebene (Basis-Layer) fadet beim Reinscrollen von 0 auf 1,
// bis die Section die Browsermitte erreicht.
function createStackParallaxAnimation(section) {
    const media = section.querySelector(".section-text-on-image__media");
    const layers = gsap.utils.toArray(section.querySelectorAll(".section-text-on-image__stack-layer"));
    if (layers.length < 2) return;

    // Grundskalierung, damit beim (kleinen) Versatz keine Kanten frei werden.
    const BASE_SCALE = 1.12;
    // Getrennte Maximal-Versätze in % der eigenen Kantenlänge. Der Gesamtweg ist
    // jeweils 2× dieser Wert (von + nach -). Bewusst sehr klein und Y >> X.
    const MAX_SHIFT_X = 1.5;
    const MAX_SHIFT_Y = 5;

    const count = layers.length;

    layers.forEach((layer, index) => {
        const img = layer.querySelector(".media__media-element, img");
        if (!img) return;

        // Focal Point aus den vom Twig gesetzten Custom-Props lesen (Fallback: Mitte).
        const style = getComputedStyle(img);
        const fx = parseFloat(style.getPropertyValue("--media-focal-point-x")) || 50;
        const fy = parseFloat(style.getPropertyValue("--media-focal-point-y")) || 50;

        // Für den Skalierungs-Anker den Focal Point leicht zur Mitte begrenzen, damit
        // die Grundskalierung auf JEDER Seite genug Überstand lässt (ein exakter
        // Eck-Focal-Point würde sonst auf der Ankerseite keinen Puffer geben und beim
        // Versatz eine Kante freilegen). Der Bildausschnitt (object-position) nutzt
        // weiterhin den echten Focal Point.
        const clamp = (v) => Math.min(85, Math.max(15, v));

        // Tiefenfaktor: Basis-Layer (index 0) = hinten = 0, vorderster = 1.
        const depth = count > 1 ? index / (count - 1) : 0;
        const shiftX = MAX_SHIFT_X * depth;
        const shiftY = MAX_SHIFT_Y * depth;

        // Überwiegend vertikale Drift: Start unten (y+, minimal x-), Ziel oben (y-, x+).
        gsap.set(img, {
            transformOrigin: `${clamp(fx)}% ${clamp(fy)}%`,
            scale: BASE_SCALE,
            xPercent: -shiftX,
            yPercent: shiftY,
            force3D: true,
        });

        gsap.to(img, {
            xPercent: shiftX,
            yPercent: -shiftY,
            ease: "none",
            scrollTrigger: {
                trigger: media,
                start: "top bottom",
                end: "bottom top",
                scrub: true,
                invalidateOnRefresh: true,
            },
        });
    });

    // Unterste Ebene (Basis-Layer): beim Reinscrollen von opacity 0 auf 1 faden,
    // von „Section-Oberkante betritt den Viewport unten" bis „Section-Oberkante an
    // der Browsermitte". Läuft auf dem Layer-Wrapper, damit es die object-position/
    // Skalierung des inneren <img> nicht anfasst.
    const baseLayer = section.querySelector(".section-text-on-image__stack-layer--base");
    if (baseLayer) {
        gsap.fromTo(
            baseLayer,
            { autoAlpha: 0 },
            {
                autoAlpha: 1,
                ease: "none",
                scrollTrigger: {
                    trigger: media,
                    start: "top bottom",
                    end: "top center",
                    scrub: true,
                    invalidateOnRefresh: true,
                },
            }
        );
    }
}

export const init = () => {
    gsap.utils
        .toArray('.section-text-on-image[data-variant="zoom"]')
        .forEach(createZoomAnimation);

    // Einzelbild-Parallax-Variante nur greifen lassen, wenn KEIN Stack vorliegt.
    gsap.utils
        .toArray('.section-text-on-image[data-variant="parallax"]:not(.section-text-on-image--media-stack)')
        .forEach(createParallaxAnimation);

    gsap.utils
        .toArray('.section-text-on-image[data-variant="reveal"]')
        .forEach(createRevealAnimation);

    // Media-Stack (≥2 Assets): X-Achsen-Parallax mit gestaffelter Tiefe, unabhängig
    // von der gewählten textOnImageVariant.
    gsap.utils
        .toArray('.section-text-on-image--media-stack')
        .forEach(createStackParallaxAnimation);
};

export default init;
